// src/pages/Nextsteps.jsx

import { useEffect, useState } from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ensureProvidersSynced } from '../database/providerSync';
import { getAllProviders } from '../database/providerDirectory';
import zipCoords from '../database/zipCoords';
import "../styles/nextSteps.css";

// Haversine distance in miles between two lat/lng points
const toRad = (deg) => (deg * Math.PI) / 180;

const distanceMiles = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8; // Earth radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

function Nextsteps() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const mode = location.state?.mode ?? (location.state?.from === '/screening' ? 'screening' : 'diagnostic');

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patientZip, setPatientZip] = useState('');

  // Filter panel state
  const [filterOpen, setFilterOpen] = useState(false);
  const [pendingRadius, setPendingRadius] = useState(30);
  const [pendingSpecialists, setPendingSpecialists] = useState([]);
  const [appliedRadius, setAppliedRadius] = useState(30);
  const [appliedSpecialists, setAppliedSpecialists] = useState([]);

  const previousPath = mode === 'screening' ? '/results/screening' : '/results/diagnostic';

  const CATEGORY_OPTIONS = [
    { key: 'audiologist', label: t('nextsteps.filters.providerType.audiologist') },
    { key: 'entPhysician', label: t('nextsteps.filters.providerType.ent') },
    { key: 'psychologist', label: t('nextsteps.filters.providerType.psychologist') },
    { key: 'hearingInstrumentSpecialist', label: t('nextsteps.filters.providerType.his') },
  ];

  const cleanText = (s) =>
    String(s || '')
      .replace(/&#\d+;|&[a-zA-Z]+;/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const normalizePostal = (postal) =>
    String(postal || '').slice(0, 5);

  const pickDisplayAddress = (p) => {
    const addresses = p.addresses || [];
    return (
      addresses.find((a) => a.address_purpose === 'LOCATION') ||
      addresses[0] ||
      {}
    );
  };

  const displayedProviders =
    appliedSpecialists.length === 0
      ? providers
      : providers.filter((p) =>
        appliedSpecialists.includes(p.providerCategoryKey)
      );



  const formatPhone = (raw) => {
    const phoneRaw = String(raw || '').replace(/\D/g, '');
    if (phoneRaw.length === 10) {
      return `(${phoneRaw.slice(0, 3)}) ${phoneRaw.slice(3, 6)}-${phoneRaw.slice(6)}`;
    }
    return phoneRaw || '';
  };

  useEffect(() => {
    let isMounted = true;

    async function loadProviders() {
      try {
        if (!isMounted) return;
        setLoading(true);
        setError(null);

        const zip = localStorage.getItem('patientZip') || '';
        setPatientZip(zip);

        // Get patient coordinates from ZIP table if possible
        const patientCoords = zip && zipCoords[zip] ? zipCoords[zip] : null;

        // Try to sync, but continue even if it fails (offline etc.)
        await ensureProvidersSynced();

        const all = await getAllProviders();

        let filtered = all || [];

        if (patientCoords) {
          // Attach distance info to every provider we have coords for
          const withDistance = filtered
            .map((p) => {
              const addr = pickDisplayAddress(p);
              const providerZip = normalizePostal(addr.postal_code);
              const providerCoords = zipCoords[providerZip];

              if (!providerCoords) return null;

              const d = distanceMiles(
                patientCoords.lat,
                patientCoords.lng,
                providerCoords.lat,
                providerCoords.lng
              );

              return { provider: p, distance: d };
            })
            .filter(Boolean);

          // Always take the 10 closest providers, regardless of distance
          const withinRadius = withDistance
            .filter((x) => x.distance <= appliedRadius)
            .sort((a, b) => a.distance - b.distance);

          filtered = withinRadius.map((x) => ({
            ...x.provider,
            distance: x.distance, // optional, for display
          }));
        } else if (zip) {
          // If we do not have coords for the patient's ZIP, use exact ZIP match as fallback
          filtered = (all || []).filter((p) => {
            const addr = pickDisplayAddress(p);
            const normalized = normalizePostal(addr.postal_code);
            return normalized === zip;
          });
        }

        if (!isMounted) return;
        setProviders(filtered);
      } catch (err) {
        console.error(err);
        if (!isMounted) return;
        setError(t('nextsteps.failedToLoad'));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadProviders();
    return () => {
      isMounted = false;
    };
  }, [appliedRadius, t]);

  function togglePendingCategory(categoryKey) {
    setPendingSpecialists((prev) =>
      prev.includes(categoryKey)
        ? prev.filter((c) => c !== categoryKey)
        : [...prev, categoryKey]
    );
  }

  function handleApplyFilters() {
    setAppliedRadius(pendingRadius);
    setAppliedSpecialists(pendingSpecialists);
    setFilterOpen(false);
  }

  function handleClearFilters() {
    setPendingRadius(10);
    setPendingSpecialists([]);
  }

  return (
    <Box className="nextsteps">
      <Box className="nextsteps__topbar">
        <Button
          variant="outlined"
          data-cy="next-step_back_btn"
          onClick={() => navigate(previousPath, { state: { ...(location.state || {}), from: location.pathname } })}
          aria-label={t('nextsteps.backToResults')}
        >
          {t('nextsteps.backToResults')}
        </Button>

        <Button
          variant="contained"
          data-cy="next-step_another-test_btn"
          onClick={() => navigate('/testFlow', { state: { ...(location.state || {}), from: location.pathname } })}
          aria-label={t('results.takeAnotherTest')}
        >
          {t('results.takeAnotherTest')}
        </Button>
      </Box>

      <Box className="nextsteps__content">
        {/* WHAT THIS MEANS (STATIC) */}
        <Paper className="nextsteps__paper nextsteps__paper--info" elevation={3}>
          <Typography variant="h6" className="nextsteps__sectionTitle">
            {t('nextsteps.whatThisMeans')}
          </Typography>

          {/* Link styling wrapper */}
          <Box className="nextsteps__linkWrap">
            <Typography variant="body2" className="nextsteps__paragraph nextsteps__paragraph--tight">
              {t('nextsteps.info.pageIntro')}
            </Typography>



            <Typography variant="subtitle1" className="nextsteps__subheading">
              <strong>{t('nextsteps.info.whyReferralTitle')}</strong>
            </Typography>
            <Typography variant="body2" className="nextsteps__paragraph">
              {t('nextsteps.info.whyReferralBody')}
            </Typography>

            <Box className="nextsteps__stack">
              {/* ENT */}
              <Box>
                <Typography variant="subtitle1" className="nextsteps__miniHeading">
                  <strong>{t('nextsteps.info.ent.title')}</strong>
                </Typography>
                <Typography variant="body2" className="nextsteps__paragraph nextsteps__paragraph--small">
                  {t('nextsteps.info.ent.body')}
                </Typography>
                <Box component="ul" className="nextsteps__list">
                  <li><Typography variant="body2">{t('nextsteps.info.ent.li1')}</Typography></li>
                  <li><Typography variant="body2">{t('nextsteps.info.ent.li2')}</Typography></li>
                  <li><Typography variant="body2">{t('nextsteps.info.ent.li3')}</Typography></li>
                </Box>
                <Typography variant="body2" className="nextsteps__paragraph nextsteps__paragraph--topSmall">
                  {t('nextsteps.info.ent.footer')}
                </Typography>
              </Box>

              {/* Audiologist */}
              <Box>
                <Typography variant="subtitle1" className="nextsteps__miniHeading">
                  <strong>{t('nextsteps.info.audiologist.title')}</strong>
                </Typography>
                <Typography variant="body2" className="nextsteps__paragraph nextsteps__paragraph--small">{t('nextsteps.info.audiologist.body')}</Typography>
                <Box component="ul" className="nextsteps__list">
                  <li><Typography variant="body2">{t('nextsteps.info.audiologist.li1')}</Typography></li>
                  <li><Typography variant="body2">{t('nextsteps.info.audiologist.li2')}</Typography></li>
                  <li><Typography variant="body2">{t('nextsteps.info.audiologist.li3')}</Typography></li>
                  <li><Typography variant="body2">{t('nextsteps.info.audiologist.li4')}</Typography></li>
                  <li><Typography variant="body2">{t('nextsteps.info.audiologist.li5')}</Typography></li>
                </Box>
              </Box>

              {/* Hearing Instrument Specialist */}
              <Box>
                <Typography variant="subtitle1" className="nextsteps__miniHeading">
                  <strong>{t('nextsteps.info.hearing_instrument_specialist.title')}</strong>
                </Typography>
                <Typography variant="body2" className="nextsteps__paragraph nextsteps__paragraph--small">
                  {t('nextsteps.info.hearing_instrument_specialist.body')}
                </Typography>
                <Box component="ul" className="nextsteps__list">
                  <li><Typography variant="body2">{t('nextsteps.info.hearing_instrument_specialist.li1')}</Typography></li>
                  <li><Typography variant="body2">{t('nextsteps.info.hearing_instrument_specialist.li2')}</Typography></li>
                  <li><Typography variant="body2">{t('nextsteps.info.hearing_instrument_specialist.li3')}</Typography></li>
                  <li><Typography variant="body2">{t('nextsteps.info.hearing_instrument_specialist.li4')}</Typography></li>
                  <li><Typography variant="body2">{t('nextsteps.info.hearing_instrument_specialist.li5')}
                  </Typography></li>
                </Box>
              </Box>

              {/* Tinnitus */}
              <Box>
                <Typography variant="subtitle1" className="nextsteps__miniHeading">
                  <strong>{t('nextsteps.info.tinnitus.title')}</strong>
                </Typography>
                <Typography variant="body2">
                  {t('nextsteps.info.tinnitus.body')}
                </Typography>
              </Box>

              {/* Counselor */}
              <Box>
                <Typography variant="subtitle1" className="nextsteps__miniHeading">
                  <strong>{t('nextsteps.info.counselor.title')}</strong> <em>{t('nextsteps.info.counselor.subtitle')}</em>
                </Typography>
                <Typography variant="body2">
                  {t('nextsteps.info.counselor.body')}
                </Typography>
              </Box>

              {/* Hearing devices + funding */}
              <Box>
                <Typography variant="subtitle1" className="nextsteps__miniHeading">
                  <strong>{t('nextsteps.info.devices.title')}</strong> <em>{t('nextsteps.info.devices.subtitle')}</em>
                </Typography>
                <Typography variant="body2" className="nextsteps__paragraph nextsteps__paragraph--small">
                  {t('nextsteps.info.devices.body1')}
                </Typography>

                <Typography variant="body2" className="nextsteps__paragraph nextsteps__paragraph--small">
                  {t('nextsteps.info.devices.body2')}
                </Typography>

                <Box component="ul" className="nextsteps__list">
                  <li>
                    <Typography variant="body2">
                      {t('nextsteps.info.devices.medicaidBody')}{' '}
                      <a
                        href="https://dhhs.ne.gov/Pages/Medicaid-Services.aspx"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('nextsteps.info.devices.learnMore')}
                      </a>
                      .
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      {t('nextsteps.info.devices.banksBody')}{' '}
                      <a
                        href="https://ncdhh.nebraska.gov/content/nebraska-hearing-aid-banks"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('nextsteps.info.devices.programDetails')}
                      </a>
                      .
                    </Typography>
                  </li>
                </Box>

                <Typography variant="caption" className="nextsteps__caption">
                  {t('nextsteps.info.devices.caption')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* NEARBY PROVIDERS */}
        <Paper elevation={3} className="nextsteps__paper nextsteps__paper--providers">
          <Box className="nextsteps__providersHeader">
            <Typography variant="h6">{t('nextsteps.nearbyProviders')}</Typography>
            <Box className="nextsteps__filterWrap">
              <button
                onClick={() => {
                  if (!filterOpen) {
                    setPendingRadius(appliedRadius);
                    setPendingSpecialists([...appliedSpecialists]);
                  }
                  setFilterOpen((o) => !o);
                }}
                aria-label="Open filters"
                className={`nextsteps__filterBtn ${appliedSpecialists.length > 0 || appliedRadius !== 10
                  ? 'nextsteps__filterBtn--active'
                  : ''
                  }`}
              >
                {/* Filter icon SVG */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                {t('nextsteps.filters.title')}
                {(appliedSpecialists.length > 0 || appliedRadius !== 10) && (
                  <span className="nextsteps__filterBadge">
                    {(appliedRadius !== 10 ? 1 : 0) + appliedSpecialists.length}
                  </span>
                )}
              </button>

              {/* Filter dropdown panel */}
              {filterOpen && (
                <Box className="nextsteps__filterPanel">
                  <Typography variant="body2" className="nextsteps__filterLabel">
                    {t('nextsteps.filters.distance.title')}
                  </Typography>
                  <select
                    value={pendingRadius}
                    onChange={(e) => setPendingRadius(Number(e.target.value))}
                    className="nextsteps__select"
                  >
                    <option value={10}>{t('nextsteps.filters.distance.range', { x: 10 })}</option>
                    <option value={20}>{t('nextsteps.filters.distance.range', { x: 20 })}</option>
                    <option value={30}>{t('nextsteps.filters.distance.range', { x: 30 })}</option>
                    <option value={40}>{t('nextsteps.filters.distance.range', { x: 40 })}</option>
                    <option value={50}>{t('nextsteps.filters.distance.range', { x: 50 })}</option>
                    <option value={100}>{t('nextsteps.filters.distance.range', { x: 100 })}</option>
                    <option value={200}>{t('nextsteps.filters.distance.range', { x: 200 })}</option>
                  </select>

                  <Typography variant="body2" className="nextsteps__filterSubLabel">
                    {t('nextsteps.filters.providerType.title')}
                  </Typography>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <Box key={opt.key} className="nextsteps__filterOption">
                      <input
                        type="checkbox"
                        id={`filter-${opt.key}`}
                        checked={pendingSpecialists.includes(opt.key)}
                        onChange={() => togglePendingCategory(opt.key)}
                        className="nextsteps__checkbox"
                      />
                      <label htmlFor={`filter-${opt.key}`} className="nextsteps__checkboxLabel">
                        {opt.label}
                      </label>
                    </Box>
                  ))}

                  <Box className="nextsteps__filterActions">
                    <button
                      onClick={handleClearFilters}
                      className="nextsteps__btn nextsteps__btn--secondary"
                    >
                      {t('common.clear')}
                    </button>
                    <button
                      onClick={handleApplyFilters}
                      className="nextsteps__btn nextsteps__btn--primary"
                    >
                      {t('common.apply')}
                    </button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          {loading && (
            <Typography className="nextsteps__message">{t('nextsteps.loadingProviders')}</Typography>
          )}

          {error && (
            <Typography className="nextsteps__message" color="error">
              {error}
            </Typography>
          )}

          {!loading && !error && (
            <>
              {displayedProviders.length === 0 ? (
                <Typography className="nextsteps__message">
                  {patientZip
                    ? t('nextsteps.noProvidersNearZip', { zip: patientZip })
                    : t('nextsteps.noProviders')}
                </Typography>
              ) : (
                <Box className="nextsteps__providerList">
                  {displayedProviders.map((p) => {
                    const basic = p.basic || {};
                    const name =
                      [
                        cleanText(basic.name_prefix),
                        cleanText(basic.first_name),
                        cleanText(basic.last_name),
                      ]
                        .filter(Boolean)
                        .join(' ') ||
                      cleanText(basic.organization_name) ||
                      t('nextsteps.unknownProvider');

                    const addr = pickDisplayAddress(p);

                    const line1 = cleanText(addr.address_1);
                    const line2 = cleanText(addr.address_2);
                    const city = cleanText(addr.city);
                    const stateAbbr = cleanText(addr.state);
                    const postal = normalizePostal(addr.postal_code);
                    const phone = formatPhone(addr.telephone_number);

                    return (
                      <Box key={p.number} className="nextsteps__providerCard">
                        <Typography variant="subtitle1">{name}</Typography>

                        {(line1 || line2) && (
                          <Typography variant="body2">
                            {line1}
                            {line2 ? `, ${line2}` : ''}
                          </Typography>
                        )}

                        {(city || stateAbbr || postal) && (
                          <Typography variant="body2">
                            {[city, stateAbbr, postal].filter(Boolean).join(', ')}
                          </Typography>
                        )}

                        {phone && (
                          <Typography variant="body2">
                            {t('nextsteps.phone')}: {phone}
                          </Typography>
                        )}

                        {p.primaryTaxonomyDescription && (
                          <Typography variant="body2" className="nextsteps__specialty">
                            {t('nextsteps.specialty')}: {p.primaryTaxonomyDescription}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default Nextsteps;
